import _ from "lodash";
import {makeAutoObservable} from "mobx";
import repository from "repositories/Unit";

import {StoreState, Facet} from "types/common";
import {Unit} from "types/unit/model";
import {UnitFilterOptions, UnitSortingOptions, UnitUpdateRequest} from "types/unit/schema";

import {removeDot} from "utils";

const UnitStorage = {
  UNIT: "unit",
  RULE_UNIT: "ruleUnit"
} as const;

type UNIT_STORAGE = typeof UnitStorage[keyof typeof UnitStorage]

export default class UnitList {
  facetsFields: string[] = [
    "subject.keyword",
    "publisher.keyword",
    "checker_name.keyword",
    "modifier_name.keyword",
    "textbook.keyword"
  ];
  state: StoreState = "none";
  searchState: StoreState = "none";
  storage?: UNIT_STORAGE;
  storageState: StoreState = "none";
  units: Unit[] = [];
  unitStorage: Unit[] = [];
  facets: Facet[] = [];
  filters: UnitFilterOptions = {};
  sorting: UnitSortingOptions = {};
  onlyWaiting: boolean = false;
  checkAll: boolean = false;
  page: number = 1;
  size: number = 20;
  total: number = 0;


  constructor() {
    makeAutoObservable(this);
  }

  async setFilterStorage(storage: UNIT_STORAGE) {
    localStorage.setItem(`${storage}Filter`, JSON.stringify(this.filters))
  }

  async setSortingStorage(storage: UNIT_STORAGE) {
    localStorage.setItem(`${storage}Sorting`, JSON.stringify(this.sorting))
  }

  async getFilterStorage(storage: UNIT_STORAGE) {
    const item = localStorage.getItem(`${storage}Filter`)
    return item ? JSON.parse(item) : {}
  }

  async getSortingStorage(storage: UNIT_STORAGE) {
    const item = localStorage.getItem(`${storage}Sorting`)
    return item ? JSON.parse(item) : {}
  }

  async readyStorage(storage?: UNIT_STORAGE) {
    if (storage) {
      this.storageState = "pending";
      this.storage = storage
      this.filters = await this.getFilterStorage(storage);
      this.sorting = await this.getSortingStorage(storage);
      this.storageState = "done";
    }
  }

  async readMany(
    onlyHasQuestion?: boolean,
    returnQuestionAggs?: boolean,
    returnRuleAggs?: boolean
  ) {
    this.state = "pending";
    try {
      if (this.storage) {
        await this.setFilterStorage(this.storage);
        await this.setSortingStorage(this.storage);
      }
      const response = await repository.readMany({
          filters: removeDot(this.storage?await this.getFilterStorage(this.storage): this.filters, [".keyword", ".kwd"]),
          sorting: this.storage?await this.getSortingStorage(this.storage): this.sorting,
          return_question_aggs: returnQuestionAggs,
          return_rule_aggs: returnRuleAggs,
          waiting_only: this.onlyWaiting,
          only_has_question: onlyHasQuestion,
          page: this.page,
          size: this.size
      });
      this.units = response.data.units;
      this.total = response.data.total;

      if (Object.values(this.filters).some(v => v)) this.searchState = "done"
      else this.searchState = "none"

      await this.readFacets();

      this.state = "done";
    } catch (e) {
      this.state = "error";
      throw e;
    }
  }

  async read(unitCode: string | null | undefined) {
    try {
      if (!unitCode)
        return undefined;
      const response = await repository.read(unitCode);
      return response.data
    } catch (e) {
      throw e;
    }
  }

  async updateMany(payload: UnitUpdateRequest) {
    try {
      const p = _.cloneDeep(payload)
      const response = await repository.update(p);
      return response.data
    } catch (e) {
      throw e;
    }
  }

  async readFacets() {
    try {
      const response = await repository.readFacets({fields: this.facetsFields});
      this.facets = response.data.facets
    } catch (e) {
      throw e;
    }
  }

  setText(text: string, field: string) {
    if (!text) {
      delete this.filters[field];
      return;
    }
    this.filters[field] = text;
  }

  getFacet(field: string) {
    const facet = this.facets.filter(f => field === f.field)[0]
    if (!facet) return []
    return facet.facet
  }

  async pushFilter(field: string, item?: any) {
    if (_.isUndefined(item) || _.isNull(item) || item === "") {
      delete this.filters[field];
      return;
    }
    this.filters[field] = item["value"]?item["value"]:item;
  }

  async popFilter(field: string) {
    delete this.filters[field];
  }

  getFilter(field: string) {
    return !_.isUndefined(this.filters[field]) ? this.filters[field] : undefined;
  }

  async pushAndPopUnit(unit: Unit) {
    if (this.unitStorage.some((u) => u._id === unit._id)) {
      this.unitStorage = this.unitStorage.filter(u => u._id !== unit._id);
    } else {
      this.unitStorage.push(unit)
    }
  }

  async switchCheckAll() {
    if (this.checkAll) {
      await this.turnCheckAllOff();
    }else {
      await this.turnCheckAllOn();
    }
  }

  async turnCheckAllOn() {
    this.checkAll = true;
    this.unitStorage = this.units;
  }

  async turnCheckAllOff() {
    this.checkAll = false;
    this.unitStorage = [];
  }

  async setUnitStorage(units: Unit[]) {
    this.unitStorage = units;
  }

  async setPage(page: number) {
    this.page = page
  }

  async setSize(size: number) {
    this.page = 1;
    this.size = size;
  }

  async setSorting(field: string) {
    this.sorting[field] = this.sorting[field]? this.sorting[field] === "desc" ? "asc" : undefined: "desc"
  }

  getSorting(field: string) {
    return this.sorting[field];
  }

  async switchOnlyWaiting() {
    if (this.onlyWaiting) {
      this.onlyWaiting = false;
    } else if (!Object.entries(this.filters).length) {
      this.sorting = {};
      this.onlyWaiting = true;
    }
  }

  unitInfo(unit: Unit) {
    return `${unit.publisher} ${unit.textbook} ${unit.unit_name}`
  }

  async reset() {
    this.filters = {};
    this.sorting = {};
    this.unitStorage = [];
    this.onlyWaiting = false;
    this.checkAll = false;
    this.page = 1;
    this.size = 20;
  }

  async clear() {
    this.state = "none";
    this.searchState = "none";
    this.storageState = "none";
    this.storage = undefined;
    this.units = [];
    this.unitStorage = [];
    this.facets = [];
    this.filters = {};
    this.sorting = {};
    this.checkAll = false;
    this.page = 1;
    this.size = 20;
    this.total = 0;
    this.onlyWaiting = false;
  }

  async search() {
    this.unitStorage = [];
    this.checkAll = false;
    this.state = "none";
  }
}
